/**
 * Графики дашборда — четыре штуки, и больше не нужно.
 *
 * **Решение D151: библиотека Recharts.** Вопрос `OP7` («Recharts или
 * ECharts») был отложен подэтапом 1.7 с формулировкой «выбирает первый
 * потребитель» — вот он. Recharts выбран потому, что он декларативный и
 * состоит из React-компонентов: человек, читающий этот файл, видит
 * `<Line dataKey="revenue" />` и понимает, что нарисовано, не изучая
 * императивного API. ECharts мощнее и тяжелее; мощность здесь не нужна —
 * нужны четыре обычных графика, которые заказчик сможет починить сам
 * (D5).
 *
 * **Чего в этом файле нет: ни одного вычисления над деньгами.** Ряды
 * приходят посчитанными (§12.1), и графику остаётся привести строки к
 * числам — ровно на последнем шаге, ради отрисовки. Округление до рубля
 * здесь допустимо и нигде больше: §9.1 разрешает округление «только при
 * отображении».
 *
 * **Пустое состояние — забота графика, а не экрана.** Ряд без точек
 * рисуется пустой рамкой с объяснением (`ChartFrame` каркаса), потому
 * что пустые оси без подписи читаются как поломка.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Button, ChartFrame } from '@shell/ui';
import { formatMoney, formatNumber } from '@shell/format';
import { usePalette } from './palette';

/** Высота графика. Одна на все: разная высота рядом стоящих карточек
 *  читается как случайность, а не как замысел. */
const HEIGHT = 260;

/** Больше стольких вертикальных столбиков подписи над ними не помещаются:
 *  «850 тыс» над каждым из четырнадцати часов налезают друг на друга. */
const MAX_VERTICAL_LABELS = 8;

/** Строки в числа — последний шаг перед отрисовкой, см. докстринг. */
function toNumber(value: unknown): number {
  if (typeof value === 'number') return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}

function shortDate(value: unknown): string {
  const text = String(value ?? '');
  // `2026-09-08` → `08.09`. Год на оси не нужен: он один на весь график,
  // а место на телефоне занимает.
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
  return match ? `${match[3]}.${match[2]}` : text;
}

/** Ось денег: «187 400 ₽» на подписи не помещается, «187 тыс» помещается. */
function shortMoney(value: number): string {
  if (Math.abs(value) >= 1_000_000) return `${formatNumber(value / 1_000_000, 1)} млн`;
  if (Math.abs(value) >= 1_000) return `${formatNumber(value / 1_000)} тыс`;
  return formatNumber(value);
}

interface TooltipRow {
  name?: string;
  value?: number | string;
  color?: string;
}

/**
 * Подсказка под курсором. Своя, а не встроенная: встроенная не знает ни
 * про рубли, ни про тему каркаса, а число без единицы в финансовом
 * интерфейсе — половина ответа.
 */
function MoneyTooltip({
  active,
  payload,
  label,
  unit,
}: {
  active?: boolean;
  payload?: TooltipRow[];
  label?: unknown;
  unit: string;
}) {
  const palette = usePalette();
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div
      className="chart-tooltip"
      style={{ background: palette.surface, borderColor: palette.border }}
    >
      <div className="chart-tooltip__label">{shortDate(label)}</div>
      {payload.map((row, index) => (
        <div key={index} className="chart-tooltip__row">
          <span className="chart-tooltip__dot" style={{ background: row.color }} />
          <span>{row.name}</span>
          <strong>
            {unit === '₽' ? formatMoney(toNumber(row.value)) : formatNumber(toNumber(row.value))}
          </strong>
        </div>
      ))}
    </div>
  );
}

/* ── Ряд выручки по дням ──────────────────────────────────────────── */

export interface SeriesPoint {
  period_start?: string;
  revenue?: string | number;
  ma7?: string | number | null;
  checks?: number;
  [key: string]: unknown;
}

/**
 * Динамика выручки со скользящим средним (§9.2, `R2`).
 *
 * MA7 рисуется пунктиром и **не** сплошной линией — это не измерение, а
 * сглаживание, и путать его с фактом нельзя. У первых шести точек его
 * нет вовсе (`null`), и Recharts честно рвёт линию: среднее по трём дням
 * под именем «среднее за семь» было бы неправдой.
 */
export function RevenueSeries({
  rows,
  onPointClick,
}: {
  rows: SeriesPoint[];
  onPointClick?: (day: string) => void;
}) {
  const palette = usePalette();
  const data = useMemo(
    () =>
      rows.map((row) => ({
        day: String(row.period_start ?? ''),
        revenue: toNumber(row.revenue),
        ma7: row.ma7 === null || row.ma7 === undefined ? null : toNumber(row.ma7),
      })),
    [rows],
  );

  return (
    <ChartFrame
      title="Выручка по дням"
      isEmpty={data.length === 0}
      emptyText="За выбранный период закрытых чеков нет"
    >
      <ResponsiveContainer width="100%" height={HEIGHT}>
        <LineChart
          data={data}
          margin={{ top: 8, right: 8, bottom: 4, left: 8 }}
          onClick={(state) => {
            // `activeLabel` у Recharts объявлен как `string | number`:
            // по оси может лежать и число. У нас там дата строкой, но
            // приводить всё равно надо — иначе тип не сойдётся, а
            // приведение молча спрячет случай, когда ось однажды станет
            // числовой.
            const label = state?.activeLabel;
            if (onPointClick && label !== undefined && label !== null) {
              onPointClick(String(label));
            }
          }}
        >
          <CartesianGrid stroke={palette.grid} strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="day"
            tickFormatter={shortDate}
            stroke={palette.label}
            fontSize={12}
            tickLine={false}
          />
          <YAxis
            tickFormatter={shortMoney}
            stroke={palette.label}
            fontSize={12}
            tickLine={false}
            axisLine={false}
            width={56}
          />
          <Tooltip content={<MoneyTooltip unit="₽" />} />
          <Legend wrapperStyle={{ fontSize: 12, color: palette.label }} />
          <Line
            type="monotone"
            dataKey="revenue"
            name="Выручка"
            stroke={palette.accent}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4 }}
          />
          <Line
            type="monotone"
            dataKey="ma7"
            name="Среднее за 7 дней"
            stroke={palette.muted}
            strokeWidth={1.5}
            strokeDasharray="5 4"
            dot={false}
            connectNulls={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </ChartFrame>
  );
}

/* ── Столбики по разрезу ──────────────────────────────────────────── */

export interface BreakdownRow {
  name?: string | null;
  label?: string;
  amount?: string | number;
  quantity?: string | number;
  [key: string]: unknown;
}

/** Сколько строк горизонтального разреза видно сразу; остальные — по
 *  кнопке. Сорок категорий в одной рамке превращались в штрихкод, где не
 *  прочесть ни подписи, ни суммы. */
export const VISIBLE_ROWS = 10;

/** Высота строки горизонтального разреза: длинное название встаёт в две
 *  строки, и на меньшей высоте подписи соседей налезали друг на друга. */
const ROW_HEIGHT = 34;

/**
 * Подпись строки разреза. Ключи разные у разных метрик, и все они — из
 * `metrics/revenue.py` дословно: `name` у категорий, блюд, официантов и
 * оплат, `hour` у часов. У официанта без сопоставленного сотрудника имени
 * нет вовсе — тогда показывается его код в кассе: честнее, чем пустое
 * место.
 */
function rowName(row: BreakdownRow): string {
  if (row.name) return String(row.name);
  if (row.label) return String(row.label);
  if (typeof row.hour === 'number') return String(row.hour).padStart(2, '0');
  if (typeof row.waiter_rk7_code === 'string') return `код ${row.waiter_rk7_code}`;
  return '—';
}

/** Строки разреза — в точки графика. Отдельно от графика ради теста: в
 *  jsdom график не рисуется, а ошибка в ключе рисует пустые оси. */
export function breakdownData(rows: BreakdownRow[], valueKey: string) {
  return rows.map((row) => ({ ...row, name: rowName(row), value: toNumber(row[valueKey]) }));
}

const NARROW = '(max-width: 640px)';

/** Узкий экран — телефон или Mini App. Подписи категорий там получают
 *  меньше места, иначе на столбики не остаётся ничего. */
function useNarrow(): boolean {
  const [narrow, setNarrow] = useState(
    () => typeof window !== 'undefined' && window.matchMedia?.(NARROW).matches === true,
  );
  useEffect(() => {
    const media = window.matchMedia?.(NARROW);
    if (!media) return undefined;
    const onChange = () => setNarrow(media.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);
  return narrow;
}

/**
 * Разрез столбиками: категории, часы, официанты, способы оплаты,
 * распределение чеков.
 *
 * Горизонтальные столбики, а не вертикальные, когда подписи длинные:
 * «Горячие закуски» под вертикальным столбиком либо не помещается, либо
 * встаёт наискось, и читать её приходится, наклонив голову.
 *
 * **Высота горизонтального разреза — по числу строк**, а не одна на все:
 * в общих 260 точках десять блюд теряли половину названий, а сорок
 * категорий слипались. Сразу видно первые десять, остальные — по кнопке.
 */
export function Breakdown({
  title,
  rows,
  valueKey = 'amount',
  unit = '₽',
  horizontal = false,
  onBarClick,
  emptyText = 'За выбранный период данных нет',
}: {
  title: string;
  rows: BreakdownRow[];
  valueKey?: string;
  unit?: string;
  horizontal?: boolean;
  onBarClick?: (row: BreakdownRow) => void;
  emptyText?: string;
}) {
  const palette = usePalette();
  const narrow = useNarrow();
  const [expanded, setExpanded] = useState(false);
  const data = useMemo(() => {
    const points = breakdownData(rows, valueKey);
    // Горизонтальный разрез отвечает на вопрос «что главное» — поэтому от
    // большего к меньшему, и под кнопку уходит хвост, а не середина.
    return horizontal ? [...points].sort((a, b) => b.value - a.value) : points;
  }, [rows, valueKey, horizontal]);

  const folded = horizontal && !expanded && data.length > VISIBLE_ROWS;
  const shown = folded ? data.slice(0, VISIBLE_ROWS) : data;
  const height = horizontal ? Math.max(160, shown.length * ROW_HEIGHT + 40) : HEIGHT;
  const formatValue = unit === '₽' ? shortMoney : (value: number) => formatNumber(value);

  return (
    <ChartFrame title={title} isEmpty={data.length === 0} emptyText={emptyText}>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart
          data={shown}
          layout={horizontal ? 'vertical' : 'horizontal'}
          margin={{
            top: horizontal ? 8 : 20,
            right: horizontal ? (narrow ? 52 : 64) : 12,
            bottom: 4,
            left: 8,
          }}
        >
          <CartesianGrid stroke={palette.grid} strokeDasharray="3 3" vertical={horizontal} />
          {horizontal ? (
            <>
              <XAxis
                type="number"
                tickFormatter={formatValue}
                stroke={palette.label}
                fontSize={12}
              />
              <YAxis
                type="category"
                dataKey="name"
                stroke={palette.label}
                fontSize={narrow ? 11 : 12}
                width={narrow ? 104 : 140}
                interval={0}
                tickLine={false}
              />
            </>
          ) : (
            <>
              <XAxis dataKey="name" stroke={palette.label} fontSize={12} tickLine={false} />
              <YAxis
                tickFormatter={formatValue}
                stroke={palette.label}
                fontSize={12}
                tickLine={false}
                axisLine={false}
                width={56}
              />
            </>
          )}
          <Tooltip content={<MoneyTooltip unit={unit} />} cursor={{ fill: palette.grid }} />
          {/* Один цвет на все столбики: это одна величина в разных разрезах,
              и разный цвет у соседних читался бы как разный смысл, которого
              нет. До 11.09.2026 они красились по очереди в шесть цветов. */}
          <Bar
            dataKey="value"
            name={title}
            fill={palette.accent}
            radius={horizontal ? [0, 4, 4, 0] : [4, 4, 0, 0]}
            onClick={(entry: unknown) => onBarClick?.(entry as BreakdownRow)}
            cursor={onBarClick ? 'pointer' : undefined}
          >
            {/* Сумма — у самого столбика, а не только на оси: «сколько»
                читается сразу, без наведения мыши, которой на телефоне нет. */}
            {(horizontal || data.length <= MAX_VERTICAL_LABELS) && (
              <LabelList
                dataKey="value"
                position={horizontal ? 'right' : 'top'}
                formatter={(value: unknown) => formatValue(toNumber(value))}
                fill={palette.label}
                fontSize={12}
              />
            )}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      {horizontal && data.length > VISIBLE_ROWS && (
        <Button small variant="quiet" onClick={() => setExpanded(!expanded)}>
          {expanded ? `Свернуть до ${VISIBLE_ROWS}` : `Показать все ${data.length}`}
        </Button>
      )}
    </ChartFrame>
  );
}
